package com.videoanalyzer.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

/**
 * Professional Video Analyzer logo component.
 * Combines a play button with an analytics chart line.
 */
@Composable
fun Logo(size: LogoSize = LogoSize.MEDIUM, animate: Boolean = false) {
    val sizeValue = when (size) {
        LogoSize.SMALL -> 28
        LogoSize.MEDIUM -> 36
        LogoSize.LARGE -> 48
        LogoSize.XLARGE -> 64
    }

    Div({
        // No float animation
        style {
            width(sizeValue.px)
            height(sizeValue.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            // No glow/drop-shadow
        }
    }) {
        // Using raw HTML for SVG since Compose Web doesn't have native SVG support
        TagElement<HTMLElement>("svg", {
            attr("viewBox", "0 0 100 100")
            attr("width", "$sizeValue")
            attr("height", "$sizeValue")
        }) {
            // Gradient definitions
            TagElement<HTMLElement>("defs", {}) {
                TagElement<HTMLElement>("linearGradient", {
                    attr("id", "logoGradient")
                    attr("x1", "0%")
                    attr("y1", "0%")
                    attr("x2", "100%")
                    attr("y2", "100%")
                }) {
                    TagElement<HTMLElement>("stop", {
                        attr("offset", "0%")
                        attr("stop-color", "#FF0000")
                    }) {}
                    TagElement<HTMLElement>("stop", {
                        attr("offset", "100%")
                        attr("stop-color", "#0EA5E9")
                    }) {}
                }
            }

            // Background rounded rectangle - gradient fill
            TagElement<HTMLElement>("rect", {
                attr("width", "100")
                attr("height", "100")
                attr("rx", "18")
                attr("fill", "url(#logoGradient)")
            }) {}

            // Play button triangle - white
            TagElement<HTMLElement>("path", {
                attr("d", "M38 28 L38 72 L72 50 Z")
                attr("fill", "white")
            }) {}

            // Analytics chart line - white for contrast
            TagElement<HTMLElement>("path", {
                attr("d", "M22 78 L38 62 L54 68 L78 32")
                attr("stroke", "rgba(255,255,255,0.9)")
                attr("stroke-width", "5")
                attr("stroke-linecap", "round")
                attr("stroke-linejoin", "round")
                attr("fill", "none")
            }) {}

            // Chart endpoint dot
            TagElement<HTMLElement>("circle", {
                attr("cx", "78")
                attr("cy", "32")
                attr("r", "5")
                attr("fill", "white")
            }) {}
        }
    }
}

/**
 * Logo sizes available.
 */
enum class LogoSize {
    SMALL,   // 32px - for inline use
    MEDIUM,  // 48px - for headers
    LARGE,   // 64px - for loading screens
    XLARGE   // 80px - for auth screens
}

/**
 * Text logo - with gradient effect.
 */
@Composable
fun LogoText(size: LogoSize = LogoSize.MEDIUM) {
    val fontSize = when (size) {
        LogoSize.SMALL -> 16
        LogoSize.MEDIUM -> 18
        LogoSize.LARGE -> 22
        LogoSize.XLARGE -> 26
    }

    Span({
        style {
            fontFamily("'Sora', sans-serif")
            fontSize(fontSize.px)
            fontWeight(700)
            letterSpacing((-0.3).px)
            color(Color("#FAFAFA"))
        }
    }) {
        Text("Video")
        Span({
            style {
                color(Color("#FF0000"))
            }
        }) { Text("Score") }
    }
}

/**
 * Combined logo with icon and text.
 */
@Composable
fun LogoWithText(size: LogoSize = LogoSize.MEDIUM, animate: Boolean = false) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        Logo(size = size, animate = animate)
        LogoText(size = size)
    }
}
